import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'

type Empreendimento = {
  id: number
  empreendimento: string
  primeira_cobranca_agua: string | null
  primeira_cobranca_esgoto: string | null
  segunda_cobranca_agua: string | null
  segunda_cobranca_esgoto: string | null
  ramal_rede: string | null
  tipo_loteamento: string | null
}

type DraftRelease = {
  id: number
  version: string
  titulo: string
  status: 'rascunho'
}

type DraftChange = {
  id: number
  release_id: number
  operacao: 'INSERT' | 'UPDATE' | 'DELETE'
  record_key: string
  dados_anteriores: Record<string, unknown> | null
  dados_novos: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

type FormState = Omit<Empreendimento, 'id'>

type BulkRow = FormState

const EMPTY_FORM: FormState = {
  empreendimento: '',
  primeira_cobranca_agua: '',
  primeira_cobranca_esgoto: '',
  segunda_cobranca_agua: '',
  segunda_cobranca_esgoto: '',
  ramal_rede: '',
  tipo_loteamento: '',
}

const CSV_HEADERS = [
  'empreendimento',
  'primeira_cobranca_agua',
  'primeira_cobranca_esgoto',
  'segunda_cobranca_agua',
  'segunda_cobranca_esgoto',
  'ramal_rede',
  'tipo_loteamento',
] as const

const HEADER_ALIASES: Record<string, keyof FormState> = {
  empreendimento: 'empreendimento',
  nome: 'empreendimento',
  primeira_cobranca_agua: 'primeira_cobranca_agua',
  primeira_agua: 'primeira_cobranca_agua',
  '1_agua': 'primeira_cobranca_agua',
  primeira_cobranca_esgoto: 'primeira_cobranca_esgoto',
  primeira_esgoto: 'primeira_cobranca_esgoto',
  '1_esgoto': 'primeira_cobranca_esgoto',
  segunda_cobranca_agua: 'segunda_cobranca_agua',
  segunda_agua: 'segunda_cobranca_agua',
  '2_agua': 'segunda_cobranca_agua',
  segunda_cobranca_esgoto: 'segunda_cobranca_esgoto',
  segunda_esgoto: 'segunda_cobranca_esgoto',
  '2_esgoto': 'segunda_cobranca_esgoto',
  ramal_rede: 'ramal_rede',
  ramalrede: 'ramal_rede',
  tipo_loteamento: 'tipo_loteamento',
  tipo: 'tipo_loteamento',
}

function normalizeForm(record: Empreendimento): FormState {
  return {
    empreendimento: record.empreendimento,
    primeira_cobranca_agua: record.primeira_cobranca_agua ?? '',
    primeira_cobranca_esgoto: record.primeira_cobranca_esgoto ?? '',
    segunda_cobranca_agua: record.segunda_cobranca_agua ?? '',
    segunda_cobranca_esgoto: record.segunda_cobranca_esgoto ?? '',
    ramal_rede: record.ramal_rede ?? '',
    tipo_loteamento: record.tipo_loteamento ?? '',
  }
}

function nextPatchVersion(version: string) {
  const parts = version.split('.').map((part) => Number.parseInt(part, 10))
  if (parts.length >= 3 && parts.every(Number.isFinite)) {
    return `${parts[0]}.${parts[1]}.${parts[2] + 1}`
  }
  return '8.0.1'
}

function normalizeHeader(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/ª|º/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function parseDelimitedLine(line: string, delimiter: string) {
  const values: string[] = []
  let current = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"'
        index += 1
      } else {
        quoted = !quoted
      }
    } else if (char === delimiter && !quoted) {
      values.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  values.push(current.trim())
  return values
}

function parseBulkCsv(text: string): BulkRow[] {
  const clean = text.replace(/^\uFEFF/, '').trim()
  if (!clean) throw new Error('O arquivo CSV está vazio.')

  const lines = clean.split(/\r?\n/).filter((line) => line.trim())
  if (lines.length < 2) throw new Error('O CSV precisa ter cabeçalho e pelo menos um registro.')

  const delimiter = (lines[0].match(/;/g)?.length ?? 0) >= (lines[0].match(/,/g)?.length ?? 0) ? ';' : ','
  const rawHeaders = parseDelimitedLine(lines[0], delimiter)
  const mappedHeaders = rawHeaders.map((header) => HEADER_ALIASES[normalizeHeader(header)] ?? null)

  if (!mappedHeaders.includes('empreendimento')) {
    throw new Error('O CSV precisa conter a coluna “empreendimento”. Use o modelo disponível no painel.')
  }

  const rows: BulkRow[] = []
  const seen = new Set<string>()

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex += 1) {
    const cells = parseDelimitedLine(lines[lineIndex], delimiter)
    const row: FormState = { ...EMPTY_FORM }

    mappedHeaders.forEach((field, index) => {
      if (field) row[field] = cells[index]?.trim() ?? ''
    })

    row.empreendimento = row.empreendimento.trim()
    if (!row.empreendimento) {
      throw new Error(`Linha ${lineIndex + 1}: empreendimento não informado.`)
    }

    const key = row.empreendimento.toLocaleLowerCase('pt-BR')
    if (seen.has(key)) {
      throw new Error(`O empreendimento “${row.empreendimento}” está duplicado no CSV.`)
    }
    seen.add(key)
    rows.push(row)
  }

  return rows
}

async function upsertInChunks(rows: Record<string, unknown>[], chunkSize = 75) {
  for (let index = 0; index < rows.length; index += chunkSize) {
    const chunk = rows.slice(index, index + chunkSize)
    const result = await supabase
      .from('draft_changes')
      .upsert(chunk, { onConflict: 'release_id,dataset,record_key' })
    if (result.error) throw result.error
  }
}

export function EmpreendimentosEditor({ onBack }: { onBack: () => void }) {
  const { profile } = useAuth()
  const [records, setRecords] = useState<Empreendimento[]>([])
  const [draftRelease, setDraftRelease] = useState<DraftRelease | null>(null)
  const [drafts, setDrafts] = useState<DraftChange[]>([])
  const [search, setSearch] = useState('')
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [editing, setEditing] = useState<Empreendimento | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkFileName, setBulkFileName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [recordsResult, releaseResult] = await Promise.all([
        supabase
          .from('empreendimentos')
          .select('id,empreendimento,primeira_cobranca_agua,primeira_cobranca_esgoto,segunda_cobranca_agua,segunda_cobranca_esgoto,ramal_rede,tipo_loteamento')
          .order('empreendimento'),
        supabase
          .from('releases')
          .select('id,version,titulo,status')
          .eq('status', 'rascunho')
          .order('id', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])

      if (recordsResult.error) throw recordsResult.error
      if (releaseResult.error) throw releaseResult.error

      const currentDraft = (releaseResult.data as DraftRelease | null) ?? null
      setRecords((recordsResult.data ?? []) as Empreendimento[])
      setDraftRelease(currentDraft)

      if (currentDraft) {
        const draftResult = await supabase
          .from('draft_changes')
          .select('id,release_id,operacao,record_key,dados_anteriores,dados_novos,created_at,updated_at')
          .eq('release_id', currentDraft.id)
          .eq('dataset', 'empreendimentos')
          .order('id')
        if (draftResult.error) throw draftResult.error
        setDrafts((draftResult.data ?? []) as DraftChange[])
      } else {
        setDrafts([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar o editor.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    if (!term) return records
    return records.filter((record) =>
      Object.values(normalizeForm(record)).some((value) => String(value ?? '').toLocaleLowerCase('pt-BR').includes(term)),
    )
  }, [records, search])

  const pendingByKey = useMemo(() => new Map(drafts.map((draft) => [draft.record_key, draft])), [drafts])

  async function ensureDraftRelease() {
    if (draftRelease) return draftRelease
    if (!profile) throw new Error('Perfil não carregado.')

    const latestResult = await supabase
      .from('releases')
      .select('version')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (latestResult.error) throw latestResult.error

    const version = nextPatchVersion(latestResult.data?.version ?? '8.0.0')
    const createResult = await supabase
      .from('releases')
      .insert({
        version,
        titulo: `Atualização Suite ${version}`,
        resumo: 'Alterações preparadas pelo painel administrativo.',
        status: 'rascunho',
        created_by: profile.id,
      })
      .select('id,version,titulo,status')
      .single()

    if (createResult.error) throw createResult.error
    const created = createResult.data as DraftRelease
    setDraftRelease(created)
    return created
  }

  function openNew() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function openEdit(record: Empreendimento) {
    setEditing(record)
    setForm(normalizeForm(record))
    setFormOpen(true)
    setBulkOpen(false)
    setError('')
    setMessage('')
  }

  function closeForm() {
    setFormOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  function updateField(field: keyof FormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function saveDraft(event: FormEvent) {
    event.preventDefault()
    if (!profile) return
    const nome = form.empreendimento.trim()
    if (!nome) {
      setError('Informe o nome do empreendimento.')
      return
    }

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const newData = { ...form, empreendimento: nome }
      const recordKey = editing?.empreendimento ?? nome
      const operation: DraftChange['operacao'] = editing ? 'UPDATE' : 'INSERT'

      const result = await supabase
        .from('draft_changes')
        .upsert({
          release_id: release.id,
          dataset: 'empreendimentos',
          operacao: operation,
          record_key: recordKey,
          dados_anteriores: editing ? normalizeForm(editing) : null,
          dados_novos: newData,
          created_by: profile.id,
          updated_by: profile.id,
        }, { onConflict: 'release_id,dataset,record_key' })

      if (result.error) throw result.error
      closeForm()
      setMessage(`Alteração salva no rascunho ${release.version}. A base oficial ainda não foi modificada.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o rascunho.')
    } finally {
      setSaving(false)
    }
  }

  async function markDelete(record: Empreendimento) {
    if (!profile) return
    if (!window.confirm(`Preparar exclusão de “${record.empreendimento}” para a próxima publicação?`)) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const result = await supabase
        .from('draft_changes')
        .upsert({
          release_id: release.id,
          dataset: 'empreendimentos',
          operacao: 'DELETE',
          record_key: record.empreendimento,
          dados_anteriores: normalizeForm(record),
          dados_novos: null,
          created_by: profile.id,
          updated_by: profile.id,
        }, { onConflict: 'release_id,dataset,record_key' })
      if (result.error) throw result.error
      setMessage(`Exclusão adicionada ao rascunho ${release.version}.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão.')
    } finally {
      setSaving(false)
    }
  }

  async function markDeleteAll() {
    if (!profile || records.length === 0) return
    const confirmation = window.prompt(
      `Esta ação preparará a exclusão dos ${records.length} empreendimentos publicados. Nada será apagado até a publicação.\n\nDigite EXCLUIR ${records.length} para continuar.`,
    )
    if (confirmation !== `EXCLUIR ${records.length}`) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const release = await ensureDraftRelease()
      const changes = records.map((record) => ({
        release_id: release.id,
        dataset: 'empreendimentos',
        operacao: 'DELETE',
        record_key: record.empreendimento,
        dados_anteriores: normalizeForm(record),
        dados_novos: null,
        created_by: profile.id,
        updated_by: profile.id,
      }))
      await upsertInChunks(changes)
      setMessage(`${records.length} exclusões foram preparadas no rascunho ${release.version}. A base oficial permanece intacta até publicar.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a exclusão total.')
    } finally {
      setSaving(false)
    }
  }

  function downloadCsvTemplate() {
    const content = `${CSV_HEADERS.join(';')}\nExemplo de Empreendimento;Regra água 1;Regra esgoto 1;Regra água 2;Regra esgoto 2;Ramal/Rede;Tipo de loteamento\n`
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'modelo_empreendimentos_suite.csv'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  async function readBulkFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      setBulkText(text)
      setBulkFileName(file.name)
      setError('')
      setMessage(`Arquivo “${file.name}” carregado. Revise e clique em Preparar importação.`)
    } catch {
      setError('Não foi possível ler o arquivo selecionado.')
    }
  }

  async function prepareBulkImport() {
    if (!profile) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const rows = parseBulkCsv(bulkText)
      const release = await ensureDraftRelease()
      const officialByName = new Map(records.map((record) => [record.empreendimento.toLocaleLowerCase('pt-BR'), record]))

      const changes = rows.map((row) => {
        const official = officialByName.get(row.empreendimento.toLocaleLowerCase('pt-BR'))
        return {
          release_id: release.id,
          dataset: 'empreendimentos',
          operacao: official ? 'UPDATE' : 'INSERT',
          record_key: official?.empreendimento ?? row.empreendimento,
          dados_anteriores: official ? normalizeForm(official) : null,
          dados_novos: row,
          created_by: profile.id,
          updated_by: profile.id,
        }
      })

      await upsertInChunks(changes)
      setBulkText('')
      setBulkFileName('')
      setBulkOpen(false)
      setMessage(`${rows.length} registros foram preparados em massa no rascunho ${release.version}. Registros já existentes viram UPDATE; novos registros viram INSERT.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível preparar a importação em massa.')
    } finally {
      setSaving(false)
    }
  }

  async function discardDraft(draft: DraftChange) {
    if (!window.confirm(`Descartar a alteração pendente de “${draft.record_key}”?`)) return
    setSaving(true)
    setError('')
    try {
      const result = await supabase.from('draft_changes').delete().eq('id', draft.id)
      if (result.error) throw result.error
      setMessage('Alteração pendente descartada.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível descartar a alteração.')
    } finally {
      setSaving(false)
    }
  }

  async function publish() {
    if (!draftRelease || profile?.role !== 'administrador' || drafts.length === 0) return
    if (!window.confirm(`Publicar ${drafts.length} alteração(ões) como versão ${draftRelease.version}?`)) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      const result = await supabase.rpc('publish_release', { target_release_id: draftRelease.id })
      if (result.error) throw result.error
      setMessage(`Versão ${draftRelease.version} publicada com sucesso. Os operadores já receberão a base atualizada.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar a atualização.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="workspace-content admin-editor">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">ADMINISTRAÇÃO · EMPREENDIMENTOS</p>
          <h1>Editor de Empreendimentos</h1>
          <p className="muted">Edite individualmente ou em massa. Nada chega à operação antes da publicação do Administrador.</p>
        </div>
        <div className="heading-actions">
          {draftRelease && <span className="status-chip warning">Rascunho {draftRelease.version}</span>}
          <button className="secondary-button compact" disabled={saving} onClick={() => { setBulkOpen((current) => !current); setFormOpen(false) }}>Importar em massa</button>
          <button className="primary-button compact" disabled={saving} onClick={openNew}>+ Novo empreendimento</button>
        </div>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      <div className="editor-summary">
        <article><span>Base oficial</span><strong>{records.length}</strong><small>registros publicados</small></article>
        <article><span>Alterações pendentes</span><strong>{drafts.length}</strong><small>{draftRelease?.version ?? 'sem rascunho ativo'}</small></article>
        <article><span>Publicação</span><strong>{profile?.role === 'administrador' ? 'Liberada' : 'Somente Admin'}</strong><small>Gestão prepara; Admin publica</small></article>
      </div>

      <section className="bulk-operations-bar">
        <div>
          <strong>Operações em massa</strong>
          <span>Para substituir toda a lista: prepare a exclusão total, importe o novo CSV e publique tudo na mesma versão.</span>
        </div>
        <div className="bulk-operations-actions">
          <button className="secondary-button compact" disabled={saving} onClick={downloadCsvTemplate}>Baixar modelo CSV</button>
          <button className="danger-button compact" disabled={saving || records.length === 0} onClick={() => void markDeleteAll()}>Excluir toda a base</button>
        </div>
      </section>

      {bulkOpen && (
        <section className="editor-form-card bulk-import-card">
          <div className="pending-head">
            <div>
              <p className="eyebrow">IMPORTAÇÃO EM MASSA</p>
              <h2>Preparar vários empreendimentos</h2>
              <p className="muted">Aceita CSV separado por ponto e vírgula ou vírgula. A coluna empreendimento é obrigatória.</p>
            </div>
            <button className="text-button" onClick={() => setBulkOpen(false)}>Fechar</button>
          </div>

          <div className="bulk-file-row">
            <label className="file-picker">
              Selecionar CSV
              <input type="file" accept=".csv,text/csv" onChange={(event) => void readBulkFile(event)} />
            </label>
            <span>{bulkFileName || 'Nenhum arquivo selecionado'}</span>
          </div>

          <textarea
            className="bulk-textarea"
            value={bulkText}
            onChange={(event) => setBulkText(event.target.value)}
            placeholder="Você também pode colar aqui o conteúdo CSV..."
            rows={9}
          />

          <div className="notice info compact-note">
            Se o empreendimento já existir na base oficial, a importação prepara um UPDATE. Se não existir, prepara um INSERT. Nada é publicado automaticamente.
          </div>

          <div className="form-actions">
            <button className="secondary-button" type="button" onClick={downloadCsvTemplate}>Baixar modelo</button>
            <button className="primary-button compact" type="button" disabled={saving || !bulkText.trim()} onClick={() => void prepareBulkImport()}>
              {saving ? 'Preparando...' : 'Preparar importação'}
            </button>
          </div>
        </section>
      )}

      {drafts.length > 0 && (
        <section className="pending-panel">
          <div className="pending-head">
            <div>
              <p className="eyebrow">RASCUNHO</p>
              <h2>Alterações aguardando publicação</h2>
            </div>
            <button className="primary-button compact" disabled={saving || profile?.role !== 'administrador'} onClick={() => void publish()}>
              Publicar {draftRelease?.version}
            </button>
          </div>
          <div className="pending-list">
            {drafts.map((draft) => (
              <div className="pending-item" key={draft.id}>
                <span className={`operation-badge ${draft.operacao.toLowerCase()}`}>{draft.operacao}</span>
                <div>
                  <strong>{draft.record_key}</strong>
                  <small>{draft.operacao === 'UPDATE' && draft.dados_novos?.empreendimento !== draft.record_key ? `Novo nome: ${String(draft.dados_novos?.empreendimento ?? '')}` : 'Alteração preparada'}</small>
                </div>
                <button className="danger-link" disabled={saving} onClick={() => void discardDraft(draft)}>Descartar</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {formOpen && (
        <section className="editor-form-card">
          <div className="pending-head">
            <div>
              <p className="eyebrow">{editing ? 'EDITAR' : 'NOVO REGISTRO'}</p>
              <h2>{editing ? editing.empreendimento : 'Cadastrar empreendimento'}</h2>
            </div>
            <button className="text-button" onClick={closeForm}>Fechar</button>
          </div>
          <form className="editor-form" onSubmit={saveDraft}>
            <label className="span-2">Empreendimento<input value={form.empreendimento} onChange={(event) => updateField('empreendimento', event.target.value)} required /></label>
            <label>1ª cobrança Água<input value={form.primeira_cobranca_agua ?? ''} onChange={(event) => updateField('primeira_cobranca_agua', event.target.value)} /></label>
            <label>1ª cobrança Esgoto<input value={form.primeira_cobranca_esgoto ?? ''} onChange={(event) => updateField('primeira_cobranca_esgoto', event.target.value)} /></label>
            <label>2ª cobrança Água<input value={form.segunda_cobranca_agua ?? ''} onChange={(event) => updateField('segunda_cobranca_agua', event.target.value)} /></label>
            <label>2ª cobrança Esgoto<input value={form.segunda_cobranca_esgoto ?? ''} onChange={(event) => updateField('segunda_cobranca_esgoto', event.target.value)} /></label>
            <label>Ramal / Rede<input value={form.ramal_rede ?? ''} onChange={(event) => updateField('ramal_rede', event.target.value)} /></label>
            <label>Tipo de loteamento<input value={form.tipo_loteamento ?? ''} onChange={(event) => updateField('tipo_loteamento', event.target.value)} /></label>
            <div className="form-actions span-2">
              <button type="button" className="secondary-button" onClick={closeForm}>Cancelar</button>
              <button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Salvando...' : 'Salvar em rascunho'}</button>
            </div>
          </form>
        </section>
      )}

      <div className="admin-table-toolbar">
        <div><h2>Base oficial</h2><p className="muted">{filtered.length} de {records.length} registros</p></div>
        <input aria-label="Pesquisar empreendimentos" placeholder="Pesquisar empreendimento, cobrança ou tipo..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      {loading ? <div className="empty-state">Carregando empreendimentos...</div> : (
        <div className="admin-table-wrap">
          <table className="admin-data-table">
            <thead><tr><th>Empreendimento</th><th>1ª Água</th><th>1ª Esgoto</th><th>2ª Água</th><th>2ª Esgoto</th><th>Ramal/Rede</th><th>Tipo</th><th>Ações</th></tr></thead>
            <tbody>
              {filtered.map((record) => {
                const pending = pendingByKey.get(record.empreendimento)
                return (
                  <tr key={record.id} className={pending ? 'has-pending' : ''}>
                    <td><strong>{record.empreendimento}</strong>{pending && <span className="row-pending">{pending.operacao} pendente</span>}</td>
                    <td>{record.primeira_cobranca_agua || '—'}</td>
                    <td>{record.primeira_cobranca_esgoto || '—'}</td>
                    <td>{record.segunda_cobranca_agua || '—'}</td>
                    <td>{record.segunda_cobranca_esgoto || '—'}</td>
                    <td>{record.ramal_rede || '—'}</td>
                    <td>{record.tipo_loteamento || '—'}</td>
                    <td><div className="table-actions"><button onClick={() => openEdit(record)}>Editar</button><button className="danger-link" onClick={() => void markDelete(record)}>Excluir</button></div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
